import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {extensionItemDefaultTooltipOffset} from '../../constants'

const TwRatingContainer = tw.div<{$style: RatingControlStyle}>`
  bg-background-active
  rounded-6

  ${p => (p.$style === 'normal' ? '' : '-translate-y-2')}
  ${p => (p.$style === 'normal' ? 'p-4' : 'px-4')}
`

const TwRatingLabel = tw.div`
  px-4
  py-4
  text-xs-regular
`

const TwRatingControl = tw.div`
  flex
`

const TwStarsContainer = tw.div`
  flex
`

const TwStar = tw.div`
  px-4
  py-4
  shrink-0
  cursor-pointer
`

type RatingControlStyle = 'normal' | 'compact'

type ExtensionRatingControlProps = {
  style?: RatingControlStyle
  maxRating?: number
  initialRating?: number
  onRatingChanged?: (rating: number) => void
  isTooltipDisabled?: boolean
}

export const ExtensionRatingControl = ({
  style = 'compact',
  maxRating = 5,
  initialRating,
  onRatingChanged,
  isTooltipDisabled
}: ExtensionRatingControlProps) => {
  const initialIndex =
    initialRating !== undefined ? initialRating - 1 : undefined
  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex)

  const tooltipContent =
    initialRating !== undefined
      ? Lang.msg.extensions.ratings.youRated(Math.min(maxRating, initialRating))
      : Lang.msg.extensions.rateExtension

  const handleOnClick = (index: number) => {
    const nextRating = index + 1
    if (nextRating !== initialRating) {
      onRatingChanged?.(nextRating)
    }
  }

  const component = (
    <TwRatingContainer $style={style}>
      {style === 'normal' ? (
        <TwRatingLabel>{tooltipContent}</TwRatingLabel>
      ) : undefined}
      <TwRatingControl>
        {/* TwStarsContainer inside TwRatingControl is for correctly handle mouse leave
            event in "normal" style, when mouse leaves last start at the right side*/}
        <TwStarsContainer
          onMouseLeave={() => {
            // NOTE: When the mouse leaves the ExtensionRatingControl, two events occur:
            // 1. Tooltip hides its visibility.
            // 2. TwStarsContainer resets the selected index.
            //
            // Using requestAnimationFrame ensures Tooltip hides first,
            // and the index reset happens in the next frame, avoiding race conditions.
            // (fixes issue: https://test-ru.yougile.com/team/e773f3991465/#ENT-497)
            requestAnimationFrame(() => setSelectedIndex(undefined))
          }}
        >
          {Array.from({length: maxRating}, (_, index) => (
            <TwStar
              key={`star-${index}`}
              onMouseOver={() => setSelectedIndex(index)}
              onClick={() => handleOnClick(index)}
            >
              <Icon
                size={12}
                color={initialIndex >= index ? 'action-default' : undefined}
                icon={
                  initialIndex >= index || selectedIndex >= index
                    ? 'IconStarFilled'
                    : 'IconStar'
                }
              />
            </TwStar>
          ))}
        </TwStarsContainer>
      </TwRatingControl>
    </TwRatingContainer>
  )

  if (style === 'normal' || isTooltipDisabled) {
    return component
  }

  return (
    <Tooltip
      content={tooltipContent}
      offset={extensionItemDefaultTooltipOffset}
    >
      {component}
    </Tooltip>
  )
}
